import { useEffect } from 'react'
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import Card from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Check from "@mui/icons-material/Check";
import SectionsIcon from "@/assets/sections.svg";
import useOnboardingContext from "@/hooks/useOnboardingContext";
import FormContainer from "./FormContainer";
import { STEPS, isAppEnabledForPlan } from "@/constants/onboarding";
import WizardFooter from "./WizardFooter";
import FullStepHeader from "../FullStepHeader";
import { useContext } from 'react'
import { OnboardingContext } from '@/context/OnboardingContext'
import getSupportedSections from "@/templates/supported-sections"
import { getProvisionedResources } from '@/api/onboarding'
import { useGetSections } from '../../../../hooks/useOnboarding';

const CustomCard = styled(Card)`
  max-width: 100vw;
  background: #FFFFFF;
  border: 2px solid ${({ theme }) => theme.palette.primary.main};
  padding: 24px;
  gap: 24px;
  border-radius: 12px;
  margin-bottom: 12px;
`;

const WizardStep9 = () => {
  const { actualStep, actualSubStep, sections, setSections, storeOnboardingData: { isStoreOnboardingComplete, appChoices, planSku }, recommendedApps, selectedTemplate, setSku, sku, sortedSections, setSortedSections, checkedSortedSections, setCheckedSortedSections, page, setPage, isGrid } = useOnboardingContext();
  const { templateCategoryData } = useContext(OnboardingContext)
  const { accessToken, currentProjectId } = useOnboardingContext()
  const { data } = useGetSections(currentProjectId, accessToken)
  const DefaultSupportedSections = getSupportedSections(selectedTemplate?.id, sku, isGrid)

  useEffect(()=>{
      getProvisionedResources()
      .then(res => {
        if (res) {
          setSku(res.ProvisionedResources[0]?.Sku)
        }
      })
  }, [])

  useEffect(() => {
    // Sections that are controlled by store onboarding data (same as recommended apps)
    const appControlledSections = ['store', 'appointments', 'blog'];
    const previousAppChoices = JSON.parse(appChoices ?? "{}");

    const shouldHideSection = (sectionId) => !isAppEnabledForPlan(sectionId, planSku);

    // Check if a section should be disabled and checked based on store onboarding data.
    // Always respects the plan gate so stale `appChoices` cannot re-enable a section the plan does not allow.
    const getSectionStateFromStoreData = (sectionId) => {
      if (!appControlledSections.includes(sectionId) || shouldHideSection(sectionId)) {
        return { disabled: false, checked: false };
      }

      // If store onboarding is complete, all app-controlled sections are disabled and checked
      // if (isStoreOnboardingComplete) {
      //   return { disabled: true, checked: true };
      // }

      if (previousAppChoices[sectionId]) {
        return { disabled: true, checked: true };
      }

      return { disabled: false, checked: false };
    };

    const sortedSupportedSections = [
      // Keep header_and_introduction first
      DefaultSupportedSections[0],
      // Sort the rest of the sections except footer
      ...DefaultSupportedSections.slice(1)
        .filter(section => section.id !== 'footer')
        // Filter out sections that should be hidden based on plan
        .filter(section => !shouldHideSection(section.id))
      // Add footer as the last section
      // DefaultSupportedSections.find(section => section.id === 'footer')
    ];

    // Apply store onboarding data to sections
    const sectionsWithStoreData = sortedSupportedSections.map(section => {
      const storeState = getSectionStateFromStoreData(section.id);
      return {
        ...section,
        disabled: section.disabled || storeState.disabled,
      };
    });

    setSortedSections(sectionsWithStoreData);
    
    // Reconcile app-controlled sections with the user's plan and previous store choices.
    // Plan-disabled sections are forced false here so stale `appChoices` or hydrated saved
    // state cannot leak `store: true` (or `appointments: true`) into downstream consumers.
    setSections(prev => {
      const updatedSections = { ...prev };
      appControlledSections.forEach(sectionId => {
        if (shouldHideSection(sectionId)) {
          updatedSections[sectionId] = false;
          return;
        }
        const storeState = getSectionStateFromStoreData(sectionId);
        if (storeState.checked) {
          updatedSections[sectionId] = true;
        }
      });
      return updatedSections;
    });

    // Only initialize checkedSortedSections if it doesn't exist yet, otherwise preserve user selections
    setCheckedSortedSections(prev => {
      // If already initialized, preserve existing checked states but update disabled/hidden status
      if (prev && prev.length > 0) {
        return sectionsWithStoreData.map((section) => {
          const existingSection = prev.find(s => s.id === section.id);
          const storeState = getSectionStateFromStoreData(section.id);
          return {
            ...section,
            label:
              section.id === 'header_and_introduction'
                ? 'Home'
                : section.id === 'contact_form'
                ? 'Contact'
                : section.id === 'faq'
                ? 'FAQs'
                : section.label,
            // Preserve existing checked state, or use store state, or default for header
            checked: existingSection?.checked ?? (['header_and_introduction'].includes(section.id) || storeState.checked)
          };
        });
      }
      
      // Initial setup - set defaults
      return sectionsWithStoreData.map((section) => {
        const storeState = getSectionStateFromStoreData(section.id);
        return {
          ...section,
          label:
            section.id === 'header_and_introduction'
              ? 'Home'
              : section.id === 'contact_form'
              ? 'Contact'
              : section.id === 'faq'
              ? 'FAQs'
              : section.label,
          checked: ['header_and_introduction'].includes(section.id) || storeState.checked
        };
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStoreOnboardingComplete, appChoices, planSku, DefaultSupportedSections.length])

  // Check if all recommended apps would be disabled
  const allAppsDisabled = recommendedApps.every((app) => {
    if (isStoreOnboardingComplete) {
      return true; // All apps would be disabled
    }
    /* if (previousAppChoices[app.id]) {
      return true; // App would be disabled
    } */
    return false; // App would not be disabled
  }) && recommendedApps.length > 0;

  // Skip apps steps if all apps are disabled
  const skipAppsSteps = allAppsDisabled;

  const step = STEPS[actualStep];
  const subStep = step?.subSteps && step.subSteps[actualSubStep];
  const {
    wizard: { title, subtitle },
  } = subStep || step;

  const handleChange = (event) => {
    setSections((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
    setCheckedSortedSections((prev) => prev.map(section =>
      section.id === event.target.name
        ? { ...section, checked: event.target.checked }
        : section
    ));

    const currentPagePosition = checkedSortedSections.findIndex(item => item.id === event.target.name)
    const uncheckedPagesBefore = checkedSortedSections.slice(0, currentPagePosition).filter(item => !item.checked).length
    if (!event.target.checked) {
      setPage(currentPagePosition - uncheckedPagesBefore - 1)
    } else if (event.target.checked) {
      setPage(currentPagePosition - uncheckedPagesBefore)
    }
  };

  return (
    <Box className="flex flex-col gap-5">
      <Card style={{ borderRadius: 0 }} variant="">
        <Typography variant="h5" style={{ marginBottom: '0.2rem', fontSize: '16px' }}>
          Page selection
        </Typography>
        <FormControl component="fieldset">
          <FormGroup sx={{ gap: '7px' }}>
            {sortedSections && sortedSections.map((section, index) => (
              <FormControlLabel
                key={index}
                disabled={section.disabled}
                sx={{
                  "&.Mui-disabled .MuiFormControlLabel-label": {
                    color: "#525252",
                  },
                }}
                control={
                  <Checkbox
                    disabled={section.disabled}
                    icon={
                      <Box
                        component="span"
                        sx={(theme) => ({
                          width: 18,
                          height: 18,
                          borderRadius: "5px",
                          border: section.disabled
                            ? `2px solid ${theme.palette.action.disabled}`
                            : "2px solid #C0C4CF",
                          boxSizing: "border-box",
                          display: "inline-block",
                          verticalAlign: "middle",
                        })}
                      />
                    }
                    checkedIcon={
                      <Box
                        component="span"
                        sx={(theme) => ({
                          width: 18,
                          height: 18,
                          borderRadius: "5px",
                          bgcolor: section.disabled
                            ? "#D4D4D4"
                            : "#093844",
                          boxSizing: "border-box",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          verticalAlign: "middle",
                          color: theme.palette.common.white,
                        })}
                      >
                        <Check
                          sx={{
                            fontSize: 16,
                            "& path": {
                              stroke: "currentColor",
                              strokeWidth: 1,
                              paintOrder: "stroke fill",
                            },
                          }}
                        />
                      </Box>
                    }
                    name={section.id}
                    checked={sections[section.id]}
                    onChange={handleChange}
                    className="!py-2"
                  />
                }
                label={section.label}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Card>
    </Box>
  );
};

export default WizardStep9;
